import React, { useMemo, useState, useEffect } from 'react';
import { Upload, BedDouble, Smartphone, AlertTriangle, CheckCircle2, X, Send, Timer } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { todayKey, addDays, toLocalDateKey, parseLocalDate } from '../lib/dateHelpers';
import { plan, conflict, toMins, RULES } from '../lib/hkPriority';
import { chargeToExtra } from '../lib/hkParser';
import {
  useHkDay, readClockFile, parseClockRows, saveUpload, saveCharges, updateRoom, sendMessage, doneMessage, nowMinutes, hhmmOf, timeAgo,
  EXTRA_OPTIONS, TO_HK_KINDS, TO_RECEPTION_KINDS, fetchCleaningTimes, firstName, minutesBetween,
} from '../lib/hkService';
import { HK } from '../components/HkRoomCard';

/**
 * Housekeeping board — reception's view (Oct 2026).
 * Upload the Clock housekeeping export, see every room live, change priority
 * (Do next / Hold), set extras from the folio, late checkouts, early check-ins,
 * and message housekeeping. Housekeeping use pages/HousekeepingLive.jsx.
 */

const JOB_LABEL = { turn: 'Leaving → arriving', arr: 'Arriving', stay: 'Stayover', out: 'Leaving · nobody arriving', dirty: 'Empty, dirty', vacant: 'Empty, clean', oos: 'Out of service', in: 'Checked in today' };
const JOB_EDGE = { in: '#7FA58E', turn: HK.forest, arr: HK.forest, stay: '#C9B98F', out: '#B9AE95', dirty: '#B9AE95', vacant: '#E4DED0', oos: '#8A8A86' };
const UNSEEN_RED_MINS = 10;

export default function HousekeepingBoard({ onNavigate }) {
  const { userId } = useAuth();
  const [offset, setOffset] = useState(0);
  const day = toLocalDateKey(addDays(todayKey(), offset));
  const { rooms, messages, lastUpload, lastCharges, loading, error, reload, patchLocal } = useHkDay(day);
  const [now, setNow] = useState(new Date());
  const [selected, setSelected] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [showTimes, setShowTimes] = useState(false);
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 30000); return () => clearInterval(t); }, []);

  const nowM = offset === 0 ? nowMinutes(now) : 0;
  const p = useMemo(() => plan(rooms, nowM), [rooms, nowM]);
  const order = useMemo(() => {
    const m = {};
    [...p.next, ...p.then].forEach((r) => { m[r.room] = r.position; });
    return m;
  }, [p]);
  const why = useMemo(() => {
    const m = {};
    [...p.next, ...p.then, ...p.waiting, ...p.lodges, ...p.done].forEach((r) => { m[r.room] = r.why; });
    return m;
  }, [p]);

  const live = rooms.filter((r) => r.job !== 'oos');
  const arrivals = rooms.filter((r) => r.job === 'turn' || r.job === 'arr');
  const ready = arrivals.filter((r) => r.status === 'done');
  const cleaning = rooms.filter((r) => r.status === 'progress');
  const stillIn = rooms.filter((r) => (r.job === 'turn' || r.job === 'out') && !r.guestGone);
  const overdue = offset === 0 ? stillIn.filter((r) => !r.lateCheckout && nowM > toMins(RULES.checkout)) : [];
  const unseen = messages.filter((m) => m.direction === 'to_hk' && !m.ack_at);
  const fromHk = messages.filter((m) => m.direction === 'to_reception' && !m.done_at);
  const clashes = rooms.filter((r) => conflict(r));

  const sections = [
    ['old', 'Old corridor'], ['new', 'New corridor'], ['lodges', 'Lodges – across car park'],
  ].map(([k, label]) => ({ k, label, list: live.filter((r) => r.sectorKey === k).sort((a, b) => +a.room - +b.room) }))
    .filter((s) => s.list.length);
  const others = live.filter((r) => !['old', 'new', 'lodges'].includes(r.sectorKey));
  if (others.length) sections.push({ k: 'other', label: 'Other', list: others });
  const closedLodges = rooms.filter((r) => r.sectorKey === 'lodges' && r.job === 'oos').length;

  const current = selected ? rooms.find((r) => r.room === selected) : null;
  const longDate = parseLocalDate(day)?.toLocaleDateString('en-IE', { weekday: 'long', day: 'numeric', month: 'long' });

  return (
    <div className="min-h-full" style={{ background: HK.paper, color: HK.forest }}>
      <div className="px-4 md:px-6 py-4 flex flex-wrap items-center gap-3" style={{ background: HK.forest, color: HK.paper }}>
        <BedDouble size={26} style={{ color: HK.gold }} />
        <div className="flex-1 min-w-[220px]">
          <div className="text-2xl leading-7" style={{ fontFamily: 'Alice, Georgia, serif' }}>Housekeeping</div>
          <div className="text-[13px]" style={{ color: '#C9C3B2' }}>
            {longDate} · {lastUpload ? `housekeeping report ${hhmmOf(lastUpload.uploaded_at)}` : 'no housekeeping report yet'} · {lastCharges ? `extras report ${hhmmOf(lastCharges.uploaded_at)}` : 'no extras report yet'}
          </div>
        </div>
        <div className="flex rounded-sm overflow-hidden" style={{ border: '1px solid #4A6355' }}>
          {[[0, 'Today'], [1, 'Tomorrow']].map(([o, l]) => (
            <button key={o} type="button" onClick={() => { setOffset(o); setSelected(null); }} className="px-4 py-2 text-sm font-bold"
              style={offset === o ? { background: HK.paper, color: HK.forest } : { color: HK.paper }}>{l}</button>
          ))}
        </div>
        <button type="button" onClick={() => setShowUpload(true)} className="flex items-center gap-2 px-4 py-2 rounded-sm text-sm font-bold" style={{ background: HK.gold, color: '#1D2A22' }}>
          <Upload size={16} /> Upload Clock reports
        </button>
        <button type="button" onClick={() => setShowTimes(true)} className="flex items-center gap-2 px-4 py-2 rounded-sm text-sm font-bold" style={{ border: '1px solid #4A6355' }}>
          <Timer size={16} /> Cleaning times
        </button>
                <button type="button" onClick={() => onNavigate && onNavigate('hk_live')} className="flex items-center gap-2 px-4 py-2 rounded-sm text-sm font-bold" style={{ border: '1px solid #4A6355' }}>
          <Smartphone size={16} /> Housekeeping screen
        </button>
      </div>

      <div className="max-w-[1440px] mx-auto px-4 md:px-6 py-5 flex flex-col gap-5">
        {error && <div className="p-3 text-sm" style={{ background: HK.redBg, color: HK.red }}>Could not load: {error}</div>}
        {lastUpload && !lastUpload.check_ok && (
          <div className="p-3 text-sm flex gap-2" style={{ background: HK.redBg, color: HK.red }}>
            <AlertTriangle size={18} className="flex-none" />
            <div>The last upload didn't match Clock's own totals: {(lastUpload.check_messages || []).join(' ')} Check you exported the right date.</div>
          </div>
        )}
        {!loading && rooms.length === 0 && (
          <div className="p-8 text-center" style={{ background: HK.card, border: `1px solid ${HK.rule}` }}>
            <div className="text-lg font-bold">No rooms for {offset === 0 ? 'today' : 'tomorrow'} yet</div>
            <div className="text-sm mt-1" style={{ color: HK.muted }}>In Clock: Reports → Housekeeping → pick the date → download as Excel (.xlsx). Then press “Upload Clock report”.</div>
            <button type="button" onClick={() => setShowUpload(true)} className="mt-4 px-5 py-2.5 rounded-sm font-bold" style={{ background: HK.forest, color: HK.paper }}>Upload Clock report</button>
          </div>
        )}

        {rooms.length > 0 && (
          <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))' }}>
            <Kpi label="Arrival rooms ready" value={`${ready.length} of ${arrivals.length}`} sub={p.next[0] ? `Next up: room ${p.next[0].room}` : 'Nothing waiting'} />
            <Kpi label="Being cleaned now" value={cleaning.length} sub={`${live.filter((r) => r.status !== 'done' && r.job !== 'vacant' && r.job !== 'in').length} still to do`} />
            <Kpi label="Guests still in room" value={stillIn.length} sub={overdue.length ? `${overdue.length} past 11:00 – ${overdue.map((r) => r.room).join(', ')}` : 'None overdue'} alert={overdue.length > 0} />
            <Kpi label="Messages not seen" value={unseen.length} sub={unseen.length ? `Oldest ${timeAgo(unseen[unseen.length - 1].created_at, now)}` : 'All seen'} alert={unseen.some((m) => (now - new Date(m.created_at)) / 60000 > UNSEEN_RED_MINS)} />
          </div>
        )}
        {clashes.length > 0 && (
          <div className="p-3 text-sm font-semibold" style={{ background: HK.redBg, color: HK.red, border: `2px solid ${HK.red}` }}>
            Late checkout clash: {clashes.map((r) => `room ${r.room} (out ${r.lateCheckout}, next guest ${r.eta || RULES.checkin})`).join('; ')} – not enough time to clean. Move the arriving guest or shorten the late checkout.
          </div>
        )}

        {rooms.length > 0 && (
          <div className="flex flex-wrap gap-5 items-start">
            <div className="flex-[999_1_560px] min-w-0 flex flex-col gap-4">
              <div className="flex flex-wrap gap-4 text-[13px]" style={{ color: HK.muted }}>
                <span>Number = cleaning order</span><span>Red outline = needs attention</span><span>Click a room to change priority, extras or late checkout</span>
                {closedLodges > 0 && <span>{closedLodges} lodges closed (hidden)</span>}
              </div>
              {sections.map((s) => (
                <div key={s.k} className="flex flex-col gap-2">
                  <div className="text-xs font-bold tracking-[2px]" style={{ color: HK.muted }}>{s.label.toUpperCase()}</div>
                  <div className="grid gap-2.5" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))' }}>
                    {s.list.map((r) => <Tile key={r.room} r={r} pos={order[r.room]} why={why[r.room] || []} active={selected === r.room} onClick={() => setSelected(r.room)} />)}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex-[1_1_340px] min-w-0 flex flex-col gap-4">
              {current
                ? <RoomPanel key={current.room} r={current} day={day} userId={userId} why={why[current.room] || []} pos={order[current.room]} patchLocal={patchLocal} onClose={() => setSelected(null)} />
                : <SendPanel day={day} userId={userId} rooms={live} />}
              <Messages messages={messages} userId={userId} now={now} fromHk={fromHk} />
            </div>
          </div>
        )}
      </div>

      {showTimes && <TimesDialog day={day} onClose={() => setShowTimes(false)} />}
      {showUpload && <UploadDialog day={day} userId={userId} onClose={() => setShowUpload(false)} onDone={() => { setShowUpload(false); reload(); }} />}
    </div>
  );
}

function Kpi({ label, value, sub, alert }) {
  return (
    <div className="px-4 py-3 rounded-sm flex flex-col gap-0.5" style={{ background: HK.card, boxShadow: `inset 0 0 0 ${alert ? 2 : 1}px ${alert ? HK.red : HK.rule}` }}>
      <div className="text-[13px] font-semibold" style={{ color: HK.muted }}>{label}</div>
      <div className="text-[32px] leading-10" style={{ fontFamily: 'Alice, Georgia, serif' }}>{value}</div>
      <div className="text-[13px]" style={{ color: alert ? HK.red : HK.muted, fontWeight: alert ? 600 : 400 }}>{sub}</div>
    </div>
  );
}

function Tile({ r, pos, why, active, onClick }) {
  const s = r.status;
  const urgent = s !== 'done' && why.some((w) => w.tone === 'urgent');
  const line = why.find((w) => w.tone === 'urgent') || why.find((w) => w.tone === 'warn') || why[0];
  return (
    <button type="button" onClick={onClick} className="text-left p-2.5 rounded-sm flex flex-col gap-1"
      style={{
        background: s === 'done' ? HK.okBg : HK.card,
        borderTop: `5px solid ${JOB_EDGE[r.job] || HK.rule}`,
        boxShadow: active ? `inset 0 0 0 2px ${HK.gold}` : urgent ? `inset 0 0 0 2px ${HK.red}` : `inset 0 0 0 1px ${HK.rule}`,
      }}>
      <div className="flex items-baseline justify-between gap-1.5">
        <span className="text-2xl leading-6" style={{ fontFamily: 'Alice, Georgia, serif' }}>{r.room}{r.premium ? <span className="text-sm align-top" style={{ color: HK.gold }}> ★</span> : null}</span>
        <span className="text-[11px] font-bold px-1.5 py-0.5 rounded-sm"
          style={s === 'done' ? { background: HK.ok, color: '#fff' } : s === 'progress' ? { background: HK.goldBg, color: HK.goldText } : { color: HK.muted, boxShadow: `inset 0 0 0 1px ${HK.rule}` }}>
          {s === 'done' ? 'Clean ✓' : s === 'progress' ? 'Cleaning' : pos ? `#${pos}` : r.job === 'vacant' ? 'Ready' : r.job === 'in' ? 'Guest in' : 'Waiting'}
        </span>
      </div>
      <div className="text-xs" style={{ color: HK.muted }}>{JOB_LABEL[r.job]}</div>
      {line && <div className="text-[13px] leading-4 font-semibold" style={{ color: line.tone === 'urgent' ? HK.red : line.tone === 'warn' ? HK.amber : HK.forest }}>{line.text}</div>}
      {r.override && <div className="text-[11px] font-extrabold" style={{ color: r.override === 'next' ? HK.red : HK.amber }}>{r.override === 'next' ? 'PINNED: DO NEXT' : 'ON HOLD'}</div>}
      {r.extras.length > 0 && <div className="text-xs font-bold" style={{ color: HK.goldText }}>{r.extras.join(', ')}</div>}
      {r.cleaner && s !== 'todo' && <div className="text-xs" style={{ color: HK.muted }}>{firstName(r.cleaner)}{s === 'done' && minutesBetween(r.started_at, r.done_at) != null ? ` · ${minutesBetween(r.started_at, r.done_at)} min` : ''}</div>}
    </button>
  );
}

function RoomPanel({ r, day, userId, why, pos, patchLocal, onClose }) {
  const [holdReason, setHoldReason] = useState(r.hold_reason || '');
  const [late, setLate] = useState(r.late_checkout_manual || '');
  const [eta, setEta] = useState(r.eta_manual || '');
  const save = async (patch, action) => {
    patchLocal(r.room, patch);
    try { await updateRoom(day, r.room, patch, userId, action); } catch (e) { alert(`Could not save: ${e.message}`); }
  };
  const tell = (kind, body) => sendMessage({ day, room: r.room, direction: 'to_hk', kind, body, userId }).catch((e) => alert(e.message));
  const toggleExtra = (x) => {
    const has = (r.extras_manual || []).includes(x);
    const next = has ? r.extras_manual.filter((e) => e !== x) : [...(r.extras_manual || []), x];
    save({ extras_manual: next }, has ? 'extra_removed' : 'extra_added');
    if (!has && day === todayKey() && r.status !== 'done') tell('add_extra', x);
  };
  const btn = 'min-h-[40px] px-3 rounded-sm text-sm font-bold';
  const outline = { background: HK.paper, color: HK.forest, border: '1px solid #B9AE95' };
  const arriving = r.job === 'turn' || r.job === 'arr';
  const leaving = r.job === 'turn' || r.job === 'out';

  return (
    <div className="p-4 flex flex-col gap-3 rounded-sm" style={{ background: HK.card, border: `1px solid ${HK.rule}` }}>
      <div className="flex items-start gap-3">
        <div className="flex-1">
          <div className="flex items-baseline gap-2">
            <span className="text-3xl" style={{ fontFamily: 'Alice, Georgia, serif' }}>{r.room}</span>
            <span className="text-sm" style={{ color: HK.muted }}>{r.type}{r.premium ? ' · ★ premium' : ''}</span>
          </div>
          <div className="text-sm font-semibold">{JOB_LABEL[r.job]}{pos ? ` · cleaning order #${pos}` : ''}</div>
          {r.cleaner && <div className="text-xs mt-0.5" style={{ color: HK.muted }}>
            Cleaned by {r.cleaner}{r.started_at ? ` · started ${hhmmOf(r.started_at)}` : ''}{r.done_at ? ` · done ${hhmmOf(r.done_at)} (${minutesBetween(r.started_at, r.done_at) ?? '?'} min)` : ''}
          </div>}
        </div>
        <button type="button" aria-label="Close room" onClick={onClose} className="p-1"><X size={18} /></button>
      </div>
      <div className="flex flex-col gap-0.5">
        {why.map((w, i) => <div key={i} className="text-sm" style={{ color: w.tone === 'urgent' ? HK.red : w.tone === 'warn' ? HK.amber : w.tone === 'ok' ? HK.ok : HK.forest, fontWeight: 600 }}>{w.text}</div>)}
        {r.comments?.length > 0 && <div className="text-xs mt-1" style={{ color: HK.muted }}>Clock comment (current guest): {r.comments.join(' · ')}</div>}
      </div>

      <Section title="Priority">
        <div className="flex flex-wrap gap-2">
          <button type="button" className={btn} style={r.override === 'next' ? { background: HK.red, color: '#fff' } : outline}
            onClick={() => { save({ override: 'next', override_at: new Date().toISOString(), override_by: userId, hold_reason: null }, 'override:next'); tell('note', 'Reception moved this room to the top – do it next'); }}>Do next</button>
          <button type="button" className={btn} style={r.override === 'hold' ? { background: HK.amber, color: '#fff' } : outline}
            onClick={() => save({ override: 'hold', override_at: new Date().toISOString(), override_by: userId, hold_reason: holdReason || null }, 'override:hold')}>Hold</button>
          <button type="button" className={btn} style={outline} disabled={!r.override}
            onClick={() => save({ override: null, override_at: null, override_by: null, hold_reason: null }, 'override:clear')}>Back to auto</button>
        </div>
        <input value={holdReason} onChange={(e) => setHoldReason(e.target.value)} onBlur={() => r.override === 'hold' && save({ hold_reason: holdReason || null }, 'hold_reason')}
          placeholder="Hold reason (e.g. maintenance)" aria-label="Hold reason" className="mt-2 w-full min-h-[38px] px-2 rounded-sm text-sm" style={{ border: '1px solid #B9AE95', background: '#fff' }} />
      </Section>

      <Section title="Guest">
        <div className="flex flex-wrap gap-2">
          {leaving && (
            <button type="button" className={btn} style={r.guest_gone ? { background: HK.ok, color: '#fff' } : outline}
              onClick={() => save({ guest_gone: !r.guest_gone, guest_gone_at: !r.guest_gone ? new Date().toISOString() : null }, r.guest_gone ? 'guest_back' : 'guest_gone')}>
              {r.guest_gone ? 'Guest gone ✓' : 'Mark guest gone'}
            </button>
          )}
          {arriving && (
            <>
              <button type="button" className={btn} style={r.guest_waiting ? { background: HK.red, color: '#fff' } : outline}
                onClick={() => { save({ guest_waiting: !r.guest_waiting }, 'guest_waiting'); if (!r.guest_waiting) tell('guest_here'); }}>
                {r.guest_waiting ? 'Guest waiting ✓' : 'Guest is here'}
              </button>
              <button type="button" className={btn} style={r.early_checkin ? { background: HK.red, color: '#fff' } : outline}
                onClick={() => save({ early_checkin: !r.early_checkin }, 'early_checkin')}>
                {r.early_checkin ? 'Early check-in ✓' : 'Early check-in'}
              </button>
            </>
          )}
          {r.dnd && <button type="button" className={btn} style={outline} onClick={() => save({ dnd: false }, 'dnd_clear')}>Clear DND</button>}
        </div>
        <div className="grid grid-cols-2 gap-2 mt-2">
          {arriving && (
            <label className="text-xs font-semibold flex flex-col gap-1" style={{ color: HK.muted }}>Arrival time {r.eta && !r.eta_manual ? `(Clock: ${r.eta})` : ''}
              <input type="time" value={eta} onChange={(e) => setEta(e.target.value)} onBlur={() => save({ eta_manual: eta || null }, 'eta')}
                className="min-h-[38px] px-2 rounded-sm text-sm" style={{ border: '1px solid #B9AE95', background: '#fff', color: HK.forest }} />
            </label>
          )}
          {leaving && (
            <label className="text-xs font-semibold flex flex-col gap-1" style={{ color: HK.muted }}>Late checkout {r.late_checkout_clock && !r.late_checkout_manual ? `(Clock: ${r.late_checkout_clock})` : r.late_checkout_booked && !r.late_checkout_manual ? '(paid – 14:00)' : ''}
              <input type="time" value={late} onChange={(e) => setLate(e.target.value)}
                onBlur={() => { if ((late || null) !== (r.late_checkout_manual || null)) { save({ late_checkout_manual: late || null }, 'late_checkout'); if (late) tell('late_checkout', `until ${late}`); } }}
                className="min-h-[38px] px-2 rounded-sm text-sm" style={{ border: '1px solid #B9AE95', background: '#fff', color: HK.forest }} />
            </label>
          )}
        </div>
      </Section>

      {(arriving || r.job === 'stay') && (
        <Section title="Extras">
          {(r.extras_charges || []).length > 0 && <div className="text-xs mb-1.5" style={{ color: HK.muted }}>From Clock charges: {r.extras_charges.join(', ')}</div>}
          {(r.charges_raw || []).length > 0 && <div className="text-[11px] mb-1.5" style={{ color: HK.muted }}>All charges on the arriving booking: {r.charges_raw.join(' · ')}</div>}
          <div className="flex flex-wrap gap-1.5">
            {EXTRA_OPTIONS.map((x) => {
              const on = (r.extras_manual || []).includes(x);
              const fromClock = (r.extras_clock || []).includes(x) || (r.extras_charges || []).includes(x);
              return (
                <button key={x} type="button" onClick={() => toggleExtra(x)} disabled={fromClock} className="min-h-[36px] px-3 rounded-sm text-sm font-bold"
                  style={on || fromClock ? { background: HK.goldBg, color: HK.goldText, boxShadow: `inset 0 0 0 2px ${HK.gold}` } : outline}>
                  {on || fromClock ? '✓ ' : ''}{x}
                </button>
              );
            })}
          </div>
        </Section>
      )}

      <Section title="Message housekeeping about this room">
        <div className="flex flex-wrap gap-2">
          <button type="button" className={btn} style={outline} onClick={() => tell('ready_check')}>Is it ready?</button>
          <QuickNote onSend={(t) => tell('note', t)} />
        </div>
      </Section>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="pt-3" style={{ borderTop: `1px solid ${HK.rule}` }}>
      <div className="text-xs font-bold tracking-wider mb-2" style={{ color: HK.muted }}>{title.toUpperCase()}</div>
      {children}
    </div>
  );
}

function QuickNote({ onSend, placeholder }) {
  const [t, setT] = useState('');
  return (
    <div className="flex gap-2 flex-1 min-w-[200px]">
      <input value={t} onChange={(e) => setT(e.target.value)} placeholder={placeholder || 'Write a message'} aria-label="Message"
        className="flex-1 min-h-[40px] px-2 rounded-sm text-sm" style={{ border: '1px solid #B9AE95', background: '#fff' }}
        onKeyDown={(e) => { if (e.key === 'Enter' && t.trim()) { onSend(t.trim()); setT(''); } }} />
      <button type="button" aria-label="Send" disabled={!t.trim()} onClick={() => { onSend(t.trim()); setT(''); }} className="px-3 rounded-sm" style={{ background: HK.forest, color: HK.paper, opacity: t.trim() ? 1 : 0.5 }}><Send size={16} /></button>
    </div>
  );
}

function SendPanel({ day, userId, rooms }) {
  return (
    <div className="p-4 flex flex-col gap-2 rounded-sm" style={{ background: HK.card, border: `1px solid ${HK.rule}` }}>
      <div className="text-lg" style={{ fontFamily: 'Alice, Georgia, serif' }}>Message all housekeeping</div>
      <div className="text-xs" style={{ color: HK.muted }}>For one room, click the room instead – you can pin it, hold it, add extras or set a late checkout there.</div>
      <QuickNote onSend={(t) => sendMessage({ day, room: null, direction: 'to_hk', kind: 'note', body: t, userId }).catch((e) => alert(e.message))} placeholder={`e.g. Wedding group arriving early (${rooms.length} rooms today)`} />
    </div>
  );
}

function Messages({ messages, userId, now, fromHk }) {
  const recent = messages.slice(0, 25);
  return (
    <div className="p-4 flex flex-col gap-2 rounded-sm" style={{ background: HK.card, border: `1px solid ${HK.rule}` }}>
      <div className="text-lg" style={{ fontFamily: 'Alice, Georgia, serif' }}>Messages {fromHk.length > 0 && <span className="text-sm font-bold" style={{ color: HK.red }}>· {fromHk.length} for you</span>}</div>
      {recent.length === 0 && <div className="text-sm" style={{ color: HK.muted }}>No messages today.</div>}
      {recent.map((m) => {
        const toHk = m.direction === 'to_hk';
        const age = (now - new Date(m.created_at)) / 60000;
        const late = toHk && !m.ack_at && age > UNSEEN_RED_MINS;
        const open = !toHk && !m.done_at;
        return (
          <div key={m.id} className="p-2.5 rounded-sm flex flex-col gap-1"
            style={{ background: late || open ? HK.redBg : HK.paper, boxShadow: late ? `inset 0 0 0 2px ${HK.red}` : 'none' }}>
            <div className="flex justify-between text-xs font-bold" style={{ color: HK.muted }}>
              <span>{toHk ? 'Reception' : 'Housekeeping'}{m.room ? ` · Room ${m.room}` : ' · Everyone'}</span><span>{hhmmOf(m.created_at)}</span>
            </div>
            <div className="text-[15px] font-semibold leading-5">{(toHk ? TO_HK_KINDS : TO_RECEPTION_KINDS)[m.kind] || 'Message'}{m.body ? ` – ${m.body}` : ''}</div>
            {toHk && <div className="text-xs font-bold" style={{ color: m.ack_at ? HK.ok : late ? HK.red : HK.amber }}>{m.ack_at ? `Seen ${hhmmOf(m.ack_at)}` : `Not seen yet · ${timeAgo(m.created_at, now)}`}</div>}
            {open && (
              <button type="button" onClick={() => doneMessage(m.id, userId)} className="self-start min-h-[34px] px-3 rounded-sm text-xs font-bold flex items-center gap-1" style={{ background: HK.forest, color: HK.paper }}>
                <CheckCircle2 size={14} /> {m.kind === 'guest_gone' ? 'Done – checked out in Clock' : 'Done'}
              </button>
            )}
            {!toHk && m.done_at && <div className="text-xs font-bold" style={{ color: HK.ok }}>Handled {hhmmOf(m.done_at)}</div>}
          </div>
        );
      })}
    </div>
  );
}

function UploadDialog({ day, userId, onClose, onDone }) {
  const [targetDay, setTargetDay] = useState(day);
  const [file, setFile] = useState(null);
  const [read, setRead] = useState(null);           // { kind, rows }
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState('');

  const pick = async (f) => {
    setErr(''); setRead(null); setResult(''); setFile(f);
    if (!f) return;
    try { setRead(await readClockFile(f)); } catch (e) { setErr(`Could not read that file: ${e.message}`); }
  };
  const parsed = useMemo(() => (read ? parseClockRows(read.kind, read.rows, targetDay) : null), [read, targetDay]);
  const isHk = read?.kind === 'hk';
  const counts = isHk && parsed ? parsed.rooms.reduce((m, r) => { m[r.job] = (m[r.job] || 0) + 1; return m; }, {}) : {};
  const arrivals = !isHk && parsed ? parsed.bookings.filter((b) => b.from === targetDay) : [];
  const withExtras = arrivals.filter((b) => b.charges.some((c) => chargeToExtra(c.name, c.qty)));
  const lateOut = !isHk && parsed ? parsed.bookings.filter((b) => b.to === targetDay && b.charges.some((c) => /late\s*check\s*-?\s*out/i.test(c.name))) : [];
  const canSave = parsed && (isHk ? parsed.rooms.length > 0 : true) && !saving;

  const save = async () => {
    setSaving(true); setErr('');
    try {
      if (isHk) {
        const res = await saveUpload(targetDay, parsed, file?.name, userId);
        setResult(`Loaded ${res.count} rooms.${res.gone ? ` ${res.gone} guest${res.gone === 1 ? '' : 's'} now checked out – marked gone.` : ''}`);
      } else {
        const res = await saveCharges(targetDay, parsed, file?.name, userId);
        setResult(`Extras updated: ${res.withExtras.length} arriving room${res.withExtras.length === 1 ? '' : 's'} with extras (${res.arrivals} arrivals checked).${res.late.length ? ` Paid late check-out (14:00): ${res.late.join(', ')}.` : ''}`);
      }
      setSaving(false);
      setTimeout(onDone, 1400);
    } catch (e) { setErr(`Save failed: ${e.message}`); setSaving(false); }
  };
  const label = (d) => parseLocalDate(d)?.toLocaleDateString('en-IE', { weekday: 'long', day: 'numeric', month: 'long' });

  return (
    <div className="fixed inset-0 z-[90] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="w-full max-w-lg max-h-[92vh] overflow-y-auto rounded-sm p-5 flex flex-col gap-4" style={{ background: HK.paper, color: HK.forest }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center">
          <div className="flex-1 text-xl" style={{ fontFamily: 'Alice, Georgia, serif' }}>Upload a Clock report</div>
          <button type="button" aria-label="Close" onClick={onClose}><X size={20} /></button>
        </div>
        <div className="text-sm flex flex-col gap-1.5" style={{ color: HK.muted }}>
          <div><b style={{ color: HK.forest }}>Housekeeping report</b> – every morning, and again any time (it picks up check-outs and check-ins). In Clock: Reports → Housekeeping → date → download Excel.</div>
          <div><b style={{ color: HK.forest }}>Rooms charges report</b> – for extras (spa → robes, bubbly &amp; chocolates, flowers, balloons) and paid late check-outs. In Clock: Reports → Rooms charges → date → download Excel. Best done the evening before.</div>
          <div>The app works out which report it is.</div>
        </div>
        <label className="text-sm font-semibold flex flex-col gap-1">Which day is it for?
          <input type="date" value={targetDay} onChange={(e) => setTargetDay(e.target.value)} className="min-h-[40px] px-2 rounded-sm" style={{ border: '1px solid #B9AE95', background: '#fff' }} />
          <span className="text-xs font-normal" style={{ color: HK.muted }}>{label(targetDay)} – the file has no date in it, so check this matches what you picked in Clock.</span>
        </label>
        <label className="text-sm font-semibold flex flex-col gap-1">Clock file (.xlsx)
          <input type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={(e) => pick(e.target.files?.[0] || null)} className="text-sm" />
        </label>
        {err && <div className="text-sm p-2" style={{ background: HK.redBg, color: HK.red }}>{err}</div>}
        {parsed && isHk && (
          <div className="text-sm p-3 rounded-sm flex flex-col gap-1" style={{ background: HK.card, border: `1px solid ${HK.rule}` }}>
            <div className="font-bold">Housekeeping report · {parsed.rooms.length} rooms</div>
            <div style={{ color: HK.muted }}>
              {counts.turn || 0} leaving → arriving · {counts.arr || 0} arriving · {counts.stay || 0} stayovers · {counts.out || 0} leaving · {counts.dirty || 0} empty dirty · {counts.in || 0} checked in · {counts.oos || 0} out of service
            </div>
            {parsed.check.ok
              ? <div className="font-semibold flex items-center gap-1" style={{ color: HK.ok }}><CheckCircle2 size={14} /> Matches Clock's own totals</div>
              : <div className="font-semibold" style={{ color: HK.red }}>{parsed.check.messages.join(' ')}</div>}
            <div className="text-xs" style={{ color: HK.muted }}>Re-uploading keeps everything already set (cleaning status, extras, late checkouts, priorities). Guest names are not stored.</div>
          </div>
        )}
        {parsed && !isHk && (
          <div className="text-sm p-3 rounded-sm flex flex-col gap-1" style={{ background: HK.card, border: `1px solid ${HK.rule}` }}>
            <div className="font-bold">Rooms charges report · {arrivals.length} arrival{arrivals.length === 1 ? '' : 's'} on {label(targetDay)}</div>
            <div style={{ color: HK.muted }}>
              {withExtras.length ? `Extras found: ${withExtras.map((b) => b.room).join(', ')}` : 'No housekeeping extras (spa = robes, bubbly & chocolates, flowers, balloons) on these arrivals.'}
            </div>
            {lateOut.length > 0 && <div style={{ color: HK.amber, fontWeight: 600 }}>Late check-out booked (leaving that day): {lateOut.map((b) => b.room).join(', ')}</div>}
            {arrivals.length === 0 && <div className="font-semibold" style={{ color: HK.red }}>No bookings in this file arrive on that date – check the date.</div>}
          </div>
        )}
        {result && <div className="text-sm font-semibold p-2" style={{ background: HK.okBg, color: HK.okText }}>{result}</div>}
        <div className="flex gap-2 justify-end">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-sm text-sm font-bold" style={{ border: '1px solid #B9AE95' }}>Close</button>
          <button type="button" disabled={!canSave} onClick={save} className="px-5 py-2 rounded-sm text-sm font-bold"
            style={{ background: HK.forest, color: HK.paper, opacity: canSave ? 1 : 0.5 }}>
            {saving ? 'Saving…' : `Load for ${label(targetDay)}`}
          </button>
        </div>
      </div>
    </div>
  );
}

const TIMES_JOB = { turn: 'Leaving → arriving', arr: 'Arriving (empty room)', stay: 'Stayover', out: 'Leaving only', dirty: 'Empty, dirty', vacant: 'Other', in: 'Other', oos: 'Other' };

function TimesDialog({ day, onClose }) {
  const [d, setD] = useState(day);
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    let live = true;
    setRows(null); setErr('');
    fetchCleaningTimes(d).then((x) => live && setRows(x)).catch((e) => live && setErr(e.message));
    return () => { live = false; };
  }, [d]);
  const timed = (rows || []).filter((r) => r.mins != null && r.mins >= 0);
  const avg = (list) => (list.length ? Math.round(list.reduce((t, r) => t + r.mins, 0) / list.length) : null);
  const group = (key) => Object.entries(timed.reduce((m, r) => { const k = key(r) || 'Not recorded'; (m[k] = m[k] || []).push(r); return m; }, {}))
    .map(([k, list]) => ({ k, n: list.length, avg: avg(list), total: list.reduce((t, r) => t + r.mins, 0) })).sort((a, b) => b.n - a.n);
  const byCleaner = group((r) => r.cleaner);
  const byJob = group((r) => TIMES_JOB[r.job]);
  const flag = (r) => (r.mins == null ? 'No start tapped' : r.mins > 60 ? 'Over 60 min' : r.mins < 10 ? 'Under 10 min – check' : '');
  const th = 'text-left text-xs font-bold tracking-wider px-2 py-1.5';
  const td = 'px-2 py-1.5 text-sm';
  const label = parseLocalDate(d)?.toLocaleDateString('en-IE', { weekday: 'long', day: 'numeric', month: 'long' });

  return (
    <div className="fixed inset-0 z-[90] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="w-full max-w-4xl max-h-[92vh] overflow-y-auto rounded-sm p-5 flex flex-col gap-4" style={{ background: HK.paper, color: HK.forest }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex-1 text-xl" style={{ fontFamily: 'Alice, Georgia, serif' }}>Cleaning times · {label}</div>
          <input type="date" value={d} onChange={(e) => setD(e.target.value)} aria-label="Date" className="min-h-[38px] px-2 rounded-sm text-sm" style={{ border: '1px solid #B9AE95', background: '#fff' }} />
          <button type="button" aria-label="Close" onClick={onClose}><X size={20} /></button>
        </div>
        {err && <div className="text-sm p-2" style={{ background: HK.redBg, color: HK.red }}>{err}</div>}
        {!rows && !err && <div className="text-sm" style={{ color: HK.muted }}>Loading…</div>}
        {rows && rows.length === 0 && <div className="text-sm" style={{ color: HK.muted }}>No rooms finished on this day yet. Times are recorded from Start to Done on the housekeeping screen.</div>}
        {rows && rows.length > 0 && (
          <>
            <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))' }}>
              <Kpi label="Rooms cleaned" value={rows.length} sub={`${timed.length} with start & finish`} />
              <Kpi label="Average per room" value={avg(timed) != null ? `${avg(timed)} min` : '–'} sub={`Target ${RULES.cleanMins} min`} alert={avg(timed) > RULES.cleanMins} />
              <Kpi label="Over 60 min" value={timed.filter((r) => r.mins > 60).length} sub="worth a look" alert={timed.some((r) => r.mins > 60)} />
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="rounded-sm overflow-hidden" style={{ background: HK.card, border: `1px solid ${HK.rule}` }}>
                <table className="w-full"><thead style={{ background: '#EFE8DA' }}><tr><th className={th}>BY PERSON</th><th className={th}>Rooms</th><th className={th}>Avg</th><th className={th}>Total</th></tr></thead>
                  <tbody>{byCleaner.map((g) => <tr key={g.k} style={{ borderTop: `1px solid ${HK.rule}` }}><td className={td}>{g.k}</td><td className={td}>{g.n}</td><td className={td}>{g.avg} min</td><td className={td}>{Math.floor(g.total / 60)}h {g.total % 60}m</td></tr>)}</tbody></table>
              </div>
              <div className="rounded-sm overflow-hidden" style={{ background: HK.card, border: `1px solid ${HK.rule}` }}>
                <table className="w-full"><thead style={{ background: '#EFE8DA' }}><tr><th className={th}>BY JOB</th><th className={th}>Rooms</th><th className={th}>Avg</th></tr></thead>
                  <tbody>{byJob.map((g) => <tr key={g.k} style={{ borderTop: `1px solid ${HK.rule}` }}><td className={td}>{g.k}</td><td className={td}>{g.n}</td><td className={td}>{g.avg} min</td></tr>)}</tbody></table>
              </div>
            </div>
            <div className="rounded-sm overflow-x-auto" style={{ background: HK.card, border: `1px solid ${HK.rule}` }}>
              <table className="w-full min-w-[620px]"><thead style={{ background: '#EFE8DA' }}><tr>
                <th className={th}>ROOM</th><th className={th}>JOB</th><th className={th}>CLEANED BY</th><th className={th}>START</th><th className={th}>DONE</th><th className={th}>MIN</th><th className={th}></th></tr></thead>
                <tbody>{rows.map((r) => (
                  <tr key={r.room} style={{ borderTop: `1px solid ${HK.rule}` }}>
                    <td className={td}><b>{r.room}</b> <span style={{ color: HK.muted }}>{r.type}</span></td>
                    <td className={td}>{TIMES_JOB[r.job]}</td>
                    <td className={td}>{r.cleaner || <span style={{ color: HK.muted }}>–</span>}</td>
                    <td className={td}>{hhmmOf(r.started_at) || '–'}</td>
                    <td className={td}>{hhmmOf(r.done_at)}</td>
                    <td className={td}><b>{r.mins ?? '–'}</b></td>
                    <td className={td} style={{ color: HK.red, fontWeight: 600 }}>{flag(r)}</td>
                  </tr>))}</tbody></table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
